import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

/* ── AI assistant (ADR-0030, ADR-0035) ── */
export type AiActionDto = { id: string; kind: string; summary: string; status: "pending" | "executed" | "rejected" | "failed" | string };

export type AiColumnType = "text" | "money" | "date" | "number";
export type AiDisplayDto =
  | {
      kind: "table";
      title: string;
      subtitle?: string;
      columns: Array<{ key: string; label: string; type?: AiColumnType }>;
      rows: Array<Record<string, string | number | null>>;
      total?: number;
      path?: string;
      source?: { tool: string; input: Record<string, unknown> };
    }
  | {
      kind: "kpis";
      title?: string;
      items: Array<{ label: string; value: number | string; type?: "money" | "number" | "text"; tone?: "neutral" | "success" | "warning" | "danger" | "primary" }>;
    };

export type AiClientActionDto =
  | { type: "navigate"; path: string; label: string }
  | { type: "quick_create"; create: string; label: string }
  | { type: "open_setting"; key: string; label: string }
  | { type: "open_help"; slug: string; label: string }
  | { type: "open_automations"; tab: "here" | "available" | "ai"; prompt?: string; label: string };

export type AiChatDto = {
  conversationId: string;
  reply: string;
  actions: AiActionDto[];
  clientActions: AiClientActionDto[];
  displays: AiDisplayDto[];
  steps: string[];
  stepLabels: string[];
  ref: string | null;
};
export type AiConversationSummaryDto = { id: string; title: string; updatedAt: string };
export type AiConversationDto = {
  id: string;
  title: string;
  items: Array<{
    role: "user" | "assistant";
    text: string;
    steps?: string[];
    ref?: string;
    displays?: AiDisplayDto[];
    clientActions?: AiClientActionDto[];
  }>;
  actions: AiActionDto[];
};
export type AiUsageDto = {
  since: string;
  totalUsd: number;
  budgetUsd: number | null;
  users: Array<{ userId: string | null; displayName: string | null; requests: number; inputTokens: number; outputTokens: number; costUsd: number }>;
};
export type AiDecisionDto = { id: string; status: string; path?: string; entity?: string };
export type AiFeedbackSurface = "assistant" | "automation_designer" | "data_entry" | "data_audit";
export type AiFeedbackDto = {
  id: string;
  surface: string;
  rating: "up" | "down";
  comment: string | null;
  question: string | null;
  answer: string | null;
  pagePath: string | null;
  status: "new" | "reviewed" | "resolved" | "dismissed";
  adminNote: string | null;
  userName: string | null;
  createdAt: string;
  updatedAt: string;
};
export type AiSavedReportDto = { id: string; title: string; tool: string; createdAt: string };

export const aiApi = {
  status(): Promise<{ configured: boolean; model: string | null }> {
    return wrap(async () => (await httpClient.get<{ item: { configured: boolean; model: string | null } }>("/ai/status")).data.item);
  },
  models(): Promise<{ choices: Array<{ id: string; label: string }>; defaultModel: string | null }> {
    return wrap(async () => (await httpClient.get<{ item: { choices: Array<{ id: string; label: string }>; defaultModel: string | null } }>("/ai/models")).data.item);
  },
  chat(input: {
    conversationId?: string | null;
    message: string;
    modelId?: string | null;
    screen?: { path: string; title?: string | null };
    context?: { settings?: Array<{ key: string; title: string }> };
  }): Promise<AiChatDto> {
    // a turn with several tool calls can take a while
    return wrap(async () => (await httpClient.post<{ item: AiChatDto }>("/ai/chat", input, { timeout: 240_000 })).data.item);
  },
  conversations(): Promise<AiConversationSummaryDto[]> {
    return wrap(async () => (await httpClient.get<{ items: AiConversationSummaryDto[] }>("/ai/conversations")).data.items);
  },
  conversation(id: string): Promise<AiConversationDto> {
    return wrap(async () => (await httpClient.get<{ item: AiConversationDto }>(`/ai/conversations/${id}`)).data.item);
  },
  deleteConversation(id: string): Promise<void> {
    return wrap(async () => {
      await httpClient.delete(`/ai/conversations/${id}`);
    });
  },
  decide(id: string, decision: "approve" | "reject"): Promise<AiDecisionDto> {
    return wrap(async () => (await httpClient.post<{ item: AiDecisionDto }>(`/ai/actions/${id}/${decision}`, {})).data.item);
  },
  usage(): Promise<AiUsageDto> {
    return wrap(async () => (await httpClient.get<{ item: AiUsageDto }>("/ai/usage")).data.item);
  },

  /* feedback */
  feedback(input: {
    surface: AiFeedbackSurface;
    refKey: string;
    rating: "up" | "down";
    comment?: string | null;
    question?: string | null;
    answer?: string | null;
    pagePath?: string | null;
  }): Promise<{ id: string; rating: string }> {
    return wrap(async () => (await httpClient.post<{ item: { id: string; rating: string } }>("/ai/feedback", input)).data.item);
  },
  myFeedback(surface: AiFeedbackSurface): Promise<Array<{ refKey: string; rating: "up" | "down" }>> {
    return wrap(async () => (await httpClient.get<{ items: Array<{ refKey: string; rating: "up" | "down" }> }>("/ai/feedback/mine", { params: { surface } })).data.items);
  },
  listFeedback(query: { rating?: "up" | "down"; status?: string } = {}): Promise<AiFeedbackDto[]> {
    return wrap(async () => (await httpClient.get<{ items: AiFeedbackDto[] }>("/ai/feedback", { params: query })).data.items);
  },
  updateFeedback(id: string, patch: { status?: AiFeedbackDto["status"]; adminNote?: string | null }): Promise<{ id: string; status: string; adminNote: string | null }> {
    return wrap(async () => (await httpClient.patch<{ item: { id: string; status: string; adminNote: string | null } }>(`/ai/feedback/${id}`, patch)).data.item);
  },

  /* saved reports */
  reports(): Promise<AiSavedReportDto[]> {
    return wrap(async () => (await httpClient.get<{ items: AiSavedReportDto[] }>("/ai/reports")).data.items);
  },
  saveReport(input: { title: string; tool: string; input: Record<string, unknown> }): Promise<AiSavedReportDto> {
    return wrap(async () => (await httpClient.post<{ item: AiSavedReportDto }>("/ai/reports", input)).data.item);
  },
  runReport(id: string): Promise<{ id: string; title: string; display: AiDisplayDto }> {
    return wrap(async () => (await httpClient.post<{ item: { id: string; title: string; display: AiDisplayDto } }>(`/ai/reports/${id}/run`, {})).data.item);
  },
  deleteReport(id: string): Promise<void> {
    return wrap(async () => {
      await httpClient.delete(`/ai/reports/${id}`);
    });
  },
};

/** Hebrew text for the AI gate errors (credits, permissions, provider) — shared by every AI screen. */
export function aiErrorText(error: unknown): string {
  const e = error as { code?: string; message?: string } | null;
  switch (e?.code) {
    case "AI_BUDGET_EXCEEDED":
      return "נגמרו הקרדיטים של ה-AI בארגון לחודש הזה. מנהל הארגון יכול לבקש רכישה בהגדרות ← קרדיטים.";
    case "AI_USER_LIMIT":
      return "הגעת למגבלה האישית החודשית שלך לשימוש ב-AI.";
    case "AI_NOT_ALLOWED":
      return "השימוש ב-AI לא הופעל עבורך בארגון הזה. פנה למנהל הארגון.";
    case "AI_DISABLED":
      return "ה-AI כבוי בארגון הזה.";
    case "AI_FEATURE_DISABLED":
      return "היכולת הזו כבויה כרגע במערכת.";
    case "AI_FEATURE_BUDGET":
      return "יכולת ה-AI הזו הגיעה לתקציב החודשי של המערכת. נסו שוב בחודש הבא.";
    case "AI_RATE_LIMITED":
      return "יותר מדי בקשות ברגע זה — נסו שוב בעוד דקה.";
    case "AI_NOT_CONFIGURED":
      return "ה-AI לא מוגדר בשרת.";
    case "AI_PROVIDER_ERROR":
      return "ספק ה-AI לא הגיב. נסו שוב בעוד רגע.";
    default:
      return e?.message || "ה-AI לא הצליח לענות כרגע";
  }
}
